"use client";
// @ts-strict-ignore

import useSWR, { useSWRConfig } from "swr";
import { CoinDetailAPI } from "services/api/coinDetail";

import { DEFAULT_TIMEFRAME } from "constants/prices";

import pick from "lodash/pick";
import filter from "lodash/filter";
import map from "lodash/map";
import sortBy from "lodash/sortBy";
import take from "lodash/take";
import {
  addPriceDataToCoinDetail,
  getAllPriceOverviewsInPricesPage,
  getExchangeOrExternalPriceData
} from "utils/prices-utils";
import { fetchAllPrices } from "utils/prices";
import { getTop9CoinsExcludingSlug } from "utils/howToBuy";
import type { Locale } from "i18n/constants";
import type { TrendingCoinObj } from "types/howToBuy-type";

const howToBuyFetcher = async (locale: string, slug: string) => {
  const [currentCoinContentfulEntries, coinPriceOverviews, allPrices] =
    await Promise.all([
      CoinDetailAPI.fetchFullEntryBySlug(slug, locale),
      getAllPriceOverviewsInPricesPage(),
      fetchAllPrices(DEFAULT_TIMEFRAME)
    ]);

  const coinEntry = coinPriceOverviews.find((entry) => entry.slug === slug);

  if (!currentCoinContentfulEntries?.items.length || !coinEntry.isTrading) {
    return {
      props: { notFound: true }
    };
  }

  const otherCoinsToBuy = getTop9CoinsExcludingSlug(allPrices, slug);
  const exchangeCoinData =
    allPrices.find(({ coin }) => coin === coinEntry.coin) || {};
  const rawPriceData = getExchangeOrExternalPriceData(
    coinEntry,
    exchangeCoinData
  );
  const initialCoinEntry = addPriceDataToCoinDetail(
    coinEntry,
    rawPriceData,
    false
  );
  const cleanedEntry = JSON.parse(JSON.stringify(initialCoinEntry));

  const trendingCoins = take(
    map(
      sortBy(
        filter(allPrices, (coin) => coin.slug !== slug),
        (coin) =>
          coin.coin === "BTC" || coin.coin === "ETH"
            ? -Infinity
            : parseFloat(coin["24hrPercentDelta"])
      ),
      (coin) =>
        pick(coin, [
          "name",
          "slug",
          "coin",
          "24hrPercentDelta",
          "ask"
        ]) as TrendingCoinObj
    ),
    10
  );

  return {
    trendingCoins,
    otherCoinsToBuy,
    currentCoinDetail: cleanedEntry,
    relatedFaqs: JSON.parse(
      JSON.stringify(
        currentCoinContentfulEntries?.items?.[0]?.fields?.relatedFaqs ?? []
      )
    )?.map((_) => _.fields)
  };
};

export const useHowToBuyDataFetcher = (slug: string, locale: Locale) => {
  const { data, error, isLoading } = useSWR(
    slug,
    (_) => howToBuyFetcher(locale, slug),
    {
      refreshInterval: 30000, // refetch every 30 seconds
      dedupingInterval: 5000
    }
  );

  const { fallback } = useSWRConfig();

  let finalData = {
    ...data
  };

  if (!Array.isArray(data?.trendingCoins) || data?.trendingCoins.length === 0) {
    finalData = { ...fallback[slug] };
  }

  return {
    data: finalData,
    error,
    isLoading
  };
};
